import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaSignOutAlt, FaChevronDown, FaChevronRight } from "react-icons/fa";
import "./navbar.css";
import companyIcon from "../../assets/images/companyIcon.png";
import { useNotifications } from "../../context/NotificationContext";

const Navbar = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [membersDropdownOpen, setMembersDropdownOpen] = useState(false);
  const [attendanceDropdownOpen, setAttendanceDropdownOpen] = useState(false);
  const [mobileMembersOpen, setMobileMembersOpen] = useState(false);
  const [mobileAttendanceOpen, setMobileAttendanceOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const membersDropdownRef = useRef(null);
  const attendanceDropdownRef = useRef(null);
  const { unreadChat, unreadTasks } = useNotifications();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setMembersDropdownOpen(false);
    setAttendanceDropdownOpen(false);
    setMobileMembersOpen(false);
    setMobileAttendanceOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (membersDropdownRef.current && !membersDropdownRef.current.contains(event.target)) {
        setMembersDropdownOpen(false);
      }
      if (attendanceDropdownRef.current && !attendanceDropdownRef.current.contains(event.target)) {
        setAttendanceDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) => {
    return location.pathname === path ? "nav-link-active" : "";
  };

  const getUserInitial = () => {
    return user?.employeeName?.charAt(0) || user?.name?.charAt(0) || 'U';
  };

  const handleMembersNavigation = (role) => {
    navigate(`/admin/employees?role=${role}`);
    setMembersDropdownOpen(false);
  };

  const handleAttendanceNavigation = (role) => {
    navigate(`/admin/attendance?role=${role}`);
    setAttendanceDropdownOpen(false);
  };

  const getCurrentRoleLabel = () => {
    const params = new URLSearchParams(location.search);
    const role = params.get('role');
    if (role === 'employee') return 'Employees';
    if (role === 'intern') return 'Interns';
    if (role === 'student') return 'Students';
    return 'Members';
  };

  const getCurrentAttendanceLabel = () => {
    const params = new URLSearchParams(location.search);
    const role = params.get('role');
    if (role === 'employee') return 'Employees Attendance';
    if (role === 'intern') return 'Interns Attendance';
    if (role === 'student') return 'Students Attendance';
    return 'Attendance';
  };

  return (
    <header className={`navbar-wrapper ${scrolled ? "navbar-scrolled" : ""}`}>
      <nav className="navbar-container">
        <Link to="/" className="navbar-brand">
          <img style={{ width: "60px" }} src={companyIcon} alt="" />
          <div className="brand-text">
            <span className="brand-title">Desired Technologies</span>
            <span className="brand-subtitle">Smart Tracker System</span>
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <div className="navbar-links desktop-only column-gap-5">
          {user?.role === "admin" ? (
            <>
              <Link to="/admin/dashboard" className={`nav-link ${isActive("/admin/dashboard")}`}>
                <span>Dashboard</span>
              </Link>
              
              {/* Members Dropdown */}
              <div className="nav-link members-dropdown-wrapper" ref={membersDropdownRef}>
                <button
                  className={`nav-link members-dropdown-btn ${location.pathname === "/admin/employees" ? "nav-link-active" : ""}`}
                  onClick={() => setMembersDropdownOpen(!membersDropdownOpen)}
                >
                  <span>{getCurrentRoleLabel()}</span>
                  <FaChevronDown style={{ fontSize: '10px', marginLeft: '4px' }} />
                </button>
                {membersDropdownOpen && (
                  <div className="members-dropdown-menu">
                    <button
                      className={`members-dropdown-item ${!location.search.includes('role=') ? 'active' : ''}`}
                      onClick={() => handleMembersNavigation('all')}
                    >
                      All Members
                    </button>
                    <button
                      className={`members-dropdown-item ${location.search.includes('role=employee') ? 'active' : ''}`}
                      onClick={() => handleMembersNavigation('employee')}
                    >
                      Employees
                    </button>
                    <button
                      className={`members-dropdown-item ${location.search.includes('role=intern') ? 'active' : ''}`}
                      onClick={() => handleMembersNavigation('intern')}
                    >
                      Interns
                    </button>
                    <button
                      className={`members-dropdown-item ${location.search.includes('role=student') ? 'active' : ''}`}
                      onClick={() => handleMembersNavigation('student')}
                    >
                      Students
                    </button>
                  </div>
                )}
              </div>

              {/* Attendance Dropdown */}
              <div className="nav-link members-dropdown-wrapper" ref={attendanceDropdownRef}>
                <button
                  className={`nav-link members-dropdown-btn ${location.pathname === "/admin/attendance" ? "nav-link-active" : ""}`}
                  onClick={() => setAttendanceDropdownOpen(!attendanceDropdownOpen)}
                >
                  <span>{getCurrentAttendanceLabel()}</span>
                  <FaChevronDown style={{ fontSize: '10px', marginLeft: '4px' }} />
                </button>
                {attendanceDropdownOpen && (
                  <div className="members-dropdown-menu">
                    <button
                      className={`members-dropdown-item ${!location.search.includes('role=') ? 'active' : ''}`}
                      onClick={() => handleAttendanceNavigation('all')}
                    >
                      All Members
                    </button>
                    <button
                      className={`members-dropdown-item ${location.search.includes('role=employee') ? 'active' : ''}`}
                      onClick={() => handleAttendanceNavigation('employee')}
                    >
                      Employees
                    </button>
                    <button
                      className={`members-dropdown-item ${location.search.includes('role=intern') ? 'active' : ''}`}
                      onClick={() => handleAttendanceNavigation('intern')}
                    >
                      Interns
                    </button>
                    <button
                      className={`members-dropdown-item ${location.search.includes('role=student') ? 'active' : ''}`}
                      onClick={() => handleAttendanceNavigation('student')}
                    >
                      Students
                    </button>
                  </div>
                )}
              </div>

              <Link to="/admin/tasks" className={`nav-link ${isActive("/admin/tasks")}`}>
                <span>Tasks</span>
                {unreadTasks > 0 && <span className="badge">{unreadTasks}</span>}
              </Link>
              <Link to="/chat" className={`nav-link ${isActive("/chat")}`}>
                <span>Chat</span>
                {unreadChat > 0 && <span className="badge">{unreadChat}</span>}
              </Link>
              <Link to="/admin/teams" className={`nav-link ${isActive("/admin/teams")}`}>
                <span>Teams</span>
              </Link>
            </>
          ) : (
            <>
              <Link to="/dashboard" className={`nav-link ${isActive("/dashboard")}`}>
                <span>Dashboard</span>
              </Link>
              <Link to="/history" className={`nav-link ${isActive("/history")}`}>
                <span>History</span>
              </Link>
              {/* ✅ NEW: My Chart link */}
              <Link to="/attendance-chart" className={`nav-link ${isActive("/attendance-chart")}`}>
                <span>My Chart</span>
              </Link>
              <Link to="/my-tasks" className={`nav-link ${isActive("/my-tasks")}`}>
                <span>My Tasks</span>
                {unreadTasks > 0 && <span className="badge">{unreadTasks}</span>}
              </Link>
              <Link to="/chat" className={`nav-link ${isActive("/chat")}`}>
                <span>Chat</span>
                {unreadChat > 0 && <span className="badge">{unreadChat}</span>}
              </Link>
            </>
          )}
        </div>

        {/* Desktop User Actions */}
        <div className="navbar-actions desktop-only">
          <div className="user-profile">
            <div className="user-avatar-wrapper">
              <div className="user-avatar">{getUserInitial()}</div>
              <div className="user-status-dot"></div>
            </div>
            <div className="user-details">
              <span className="user-name">
                {user?.employeeName || user?.name || "Guest User"}
              </span>
              <span className="user-role-badge">
                {user?.role === "admin" ? "Admin" : user?.role === "employee" ? "Employee" : user?.role === "intern" ? "Intern" : "User"}
              </span>
            </div>
          </div>
          <button onClick={onLogout} className="btn-logout" aria-label="Logout">
            <FaSignOutAlt />
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      </nav>

      {/* Mobile Navigation Drawer */}
      <div className={`mobile-menu-overlay ${isOpen ? "open" : ""}`} onClick={() => setIsOpen(false)} />
      <aside className={`mobile-menu-drawer ${isOpen ? "open" : ""}`}>
        <div className="mobile-menu-content">
          <div className="mobile-user-info">
            <div className="user-avatar large">{getUserInitial()}</div>
            <h3 className="mobile-user-name">
              {user?.employeeName || user?.name || "Guest User"}
            </h3>
            <span className="mobile-user-role">
              {user?.role === "admin" ? "Administrator" : "Employee"}
            </span>
          </div>

          <nav className="mobile-links">
            {user?.role === "admin" ? (
              <>
                <Link to="/admin/dashboard" className={`mobile-link ${isActive("/admin/dashboard")}`}>
                  <span>Dashboard</span>
                </Link>
                
                {/* Mobile Members Dropdown */}
                <div className={`mobile-link mobile-members-section ${mobileMembersOpen ? 'open' : ''}`}>
                  <span onClick={() => setMobileMembersOpen(!mobileMembersOpen)}>
                    Members
                    <FaChevronRight style={{ fontSize: '10px', marginLeft: 'auto', transition: 'transform 0.2s ease', transform: mobileMembersOpen ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                  </span>
                  <div className="mobile-members-options">
                    <Link to="/admin/employees?role=all" className="mobile-sublink">All Members</Link>
                    <Link to="/admin/employees?role=employee" className="mobile-sublink">Employees</Link>
                    <Link to="/admin/employees?role=intern" className="mobile-sublink">Interns</Link>
                    <Link to="/admin/employees?role=student" className="mobile-sublink">Students</Link>
                  </div>
                </div>

                {/* Mobile Attendance Dropdown */}
                <div className={`mobile-link mobile-members-section ${mobileAttendanceOpen ? 'open' : ''}`}>
                  <span onClick={() => setMobileAttendanceOpen(!mobileAttendanceOpen)}>
                    Attendance
                    <FaChevronRight style={{ fontSize: '10px', marginLeft: 'auto', transition: 'transform 0.2s ease', transform: mobileAttendanceOpen ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                  </span>
                  <div className="mobile-members-options">
                    <Link to="/admin/attendance?role=all" className="mobile-sublink">All Members</Link>
                    <Link to="/admin/attendance?role=employee" className="mobile-sublink">Employees</Link>
                    <Link to="/admin/attendance?role=intern" className="mobile-sublink">Interns</Link>
                    <Link to="/admin/attendance?role=student" className="mobile-sublink">Students</Link>
                  </div>
                </div>
                
                <Link to="/admin/tasks" className={`mobile-link ${isActive("/admin/tasks")}`}>
                  <span>Tasks</span>
                  {unreadTasks > 0 && <span className="badge">{unreadTasks}</span>}
                </Link>
                <Link to="/chat" className={`mobile-link ${isActive("/chat")}`}>
                  <span>Chat</span>
                  {unreadChat > 0 && <span className="badge">{unreadChat}</span>}
                </Link>
                <Link to="/admin/teams" className={`mobile-link ${isActive("/admin/teams")}`}>
                  <span>Teams</span>
                </Link>
              </>
            ) : (
              <>
                <Link to="/dashboard" className={`mobile-link ${isActive("/dashboard")}`}>
                  <span>Dashboard</span>
                </Link>
                <Link to="/history" className={`mobile-link ${isActive("/history")}`}>
                  <span>History</span>
                </Link>
                {/* ✅ NEW: My Chart mobile link */}
                <Link to="/attendance-chart" className={`mobile-link ${isActive("/attendance-chart")}`}>
                  <span>My Chart</span>
                </Link>
                <Link to="/my-tasks" className={`mobile-link ${isActive("/my-tasks")}`}>
                  <span>My Tasks</span>
                  {unreadTasks > 0 && <span className="badge">{unreadTasks}</span>}
                </Link>
                <Link to="/chat" className={`mobile-link ${isActive("/chat")}`}>
                  <span>Chat</span>
                  {unreadChat > 0 && <span className="badge">{unreadChat}</span>}
                </Link>
              </>
            )}
          </nav>

          <div className="mobile-actions">
            <button onClick={onLogout} className="btn-logout-mobile">
              <FaSignOutAlt />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>
    </header>
  );
};

export default Navbar;